import { DateFormat } from "common";
import { Dayjs } from "dayjs";
import { LineItemType, PurchaseItemObject, PurchaseStatus, Seller } from "types";
import { api } from "../api/api";
import { config } from "../config";
import { GiftStoreProductType } from "../stores/reports/giftsReportFilterStore";

export enum GiftReportValues {
    TOTAL_AMOUNT = "totalAmount",
    AMOUNT_REDEEMED = "amountRedeemed",
    OUTSTANDING_BALANCE = "outstandingBalance",
    RECOGNIZED = "recognized",
    DEFERRED = "deferred",
}

type GiftReport = { [K in GiftReportValues]: number };

export interface GiftReportParams {
    seller: Seller;
    from: Dayjs;
    to: Dayjs;
    type: GiftStoreProductType;
}

export const getGiftReport = async (params: GiftReportParams): Promise<GiftReport> => {
    const { seller } = params;
    const index = config.elasticsearch.indexes.purchase;
    const { data } = await api.post(`/elasticsearch/${index}/_search?seller=${seller.id}`, getPayload(params));

    return getReport(data);
};

const getReport = (data: any): GiftReport => {
    const report: any = {};
    for (const value of Object.values(GiftReportValues)) {
        if (value === GiftReportValues.AMOUNT_REDEEMED) {
            const amountRedeemed = data.aggregations.items[value]?.coupons?.amount?.total.value;
            report[value] = amountRedeemed ? amountRedeemed * -1 : null;
        } else if (value === GiftReportValues.TOTAL_AMOUNT) {
            report[value] = data.aggregations.items[value]?.giftValue?.amount?.total.value;
        } else {
            report[value] = data.aggregations.items[value]?.total.value;
        }
    }

    return report;
};

const getPayload = ({ seller, from, to, type }: GiftReportParams) => {
    const start = from.format(DateFormat.DATE_ISO);
    const end = to.format(DateFormat.DATE_ISO);
    const dateRange = { gte: start, lte: end, time_zone: seller.timezoneName };
    const rangeFilter: any = { range: { createdAt: dateRange } };

    return {
        size: 0,
        query: {
            bool: {
                must: [{ term: { "seller.id": seller.id } }, { ...rangeFilter }],
                must_not: { term: { status: PurchaseStatus.DRAFT } },
            },
        },
        aggs: {
            items: {
                nested: { path: "items" },
                aggs: {
                    [GiftReportValues.TOTAL_AMOUNT]: {
                        ...getAggregation("items.lineItems.amount", GiftReportValues.TOTAL_AMOUNT, type),
                    },
                    [GiftReportValues.AMOUNT_REDEEMED]: {
                        ...getAggregation("items.lineItems.amount", GiftReportValues.AMOUNT_REDEEMED, type),
                    },
                    [GiftReportValues.OUTSTANDING_BALANCE]: {
                        ...getAggregation("items.balance", GiftReportValues.OUTSTANDING_BALANCE, type),
                    },
                },
            },
        },
    };
};

const getAggregation = (field: string, type: string, productType: GiftStoreProductType) => {
    return {
        filter: {
            bool: {
                must: {
                    terms: {
                        "items.objectName": getProductObjectArray(productType),
                    },
                },
            },
        },
        aggs:
            field === "items.lineItems.amount"
                ? getNestedAmountAggregation(field, type)
                : {
                      total: {
                          sum: {
                              field,
                          },
                      },
                  },
    };
};

const getProductObjectArray = (type: GiftStoreProductType) => {
    switch (type) {
        case GiftStoreProductType.ALL:
            return [PurchaseItemObject.GIFT, PurchaseItemObject.STORE_CREDIT];
        case GiftStoreProductType.STORE_CREDIT:
            return [PurchaseItemObject.STORE_CREDIT];
        default:
            return [PurchaseItemObject.GIFT];
    }
};

const getNestedAmountAggregation = (field: string, type: string) => {
    return {
        ...(type === GiftReportValues.AMOUNT_REDEEMED
            ? { coupons: getAggs(field, type) }
            : { giftValue: getAggs(field, type) }),
    };
};

const getAggs = (field: string, type: string) => {
    const lineItemTypes =
        type === GiftReportValues.AMOUNT_REDEEMED
            ? [LineItemType.COUPON_REDEEM, LineItemType.PROMOTIONAL_DISCOUNT_REDEEM]
            : [LineItemType.GIFT, LineItemType.ADJUSTMENT];
    let filter: any = {
        terms: {
            "items.lineItems.type": lineItemTypes,
        },
    };

    if (type === GiftReportValues.TOTAL_AMOUNT) {
        filter = {
            bool: {
                must: [
                    {
                        terms: {
                            "items.lineItems.type": lineItemTypes,
                        },
                    },
                    {
                        term: {
                            "items.lineItems.includeInAmount": true,
                        },
                    },
                ],
            },
        };
    }

    return {
        nested: {
            path: "items.lineItems",
        },
        aggs: {
            amount: {
                filter,
                aggs: {
                    total: {
                        sum: {
                            field,
                        },
                    },
                },
            },
        },
    };
};
